import CalendarEvent, { CalendarItemTypeTypeEx } from 'owa-calendar-types/lib/types/CalendarEvent';
import strings from 'owa-strings/owa-calendar-strings';
import { addOrModifyItemInCache } from '../utils/SharedCacheFunctions';
import { CalendarEventCreateResult, instantCalendarEventCreate } from '../actions/publicActions';
import { ControlIcons } from 'owa-control-icons';
import { DateRange } from 'owa-datetime-utils';
import { expandRecurringItem } from '../utils/expandRecurringItem';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isEventsCacheInitialized } from '../selectors/eventsCacheSelectors';
import {
    lazyShowCalendarNotification,
    lazyShowCalendarNotificationWithButtonCallback,
} from 'owa-calendar-notification-bar';
import { orchestrator } from 'satcheljs';
import {
    addCalendarEventsWithinCurrentLockedDateRanges,
    removeCalendarEventsFromEventsCacheMatchingFilter,
} from '../actions/eventsCacheActions';
import {
    prepareItemForInstantUpdate,
    prepareSeriesItemsForInstantUpdate,
} from '../utils/InstantUpdatePreparationFunctions';

orchestrator(instantCalendarEventCreate, async actionMessage => {
    const {
        eventToCreate,
        rangeToCreate,
        actionToExecute,
        errorMessage,
        onEventCreatedFailed,
        onEventCreatedSuccess,
    } = actionMessage;

    const allowLocalCreate = isInstantCreateSupported(eventToCreate);
    onCreateItemStart(eventToCreate, allowLocalCreate, rangeToCreate);
    let retryFunction = () =>
        instantCalendarEventCreate(
            eventToCreate,
            rangeToCreate,
            actionToExecute,
            onEventCreatedFailed,
            onEventCreatedSuccess,
            errorMessage
        );

    let result: CalendarEventCreateResult = null;
    try {
        result = await actionToExecute(eventToCreate);
    } catch (error) {
        // Exception, assume create failed
        onCreateItemFailed(eventToCreate, errorMessage, allowLocalCreate, retryFunction);
        onEventCreatedFailed(error);
        throw error;
    }

    let createdItem: CalendarEvent = null;
    if (result.isSuccessful) {
        createdItem = result.createdItem;
        onCreateItemSucceed(eventToCreate, createdItem, allowLocalCreate);
    } else {
        // Either response failed or we got no item back
        onCreateItemFailed(eventToCreate, errorMessage, allowLocalCreate, retryFunction);
        onEventCreatedFailed(errorMessage);
        throw errorMessage;
    }

    onEventCreatedSuccess(createdItem);
});

function isInstantCreateSupported(itemBeforeCreate: CalendarEvent): boolean {
    // do not support instant create for unselected calendars that have no cache.
    return isEventsCacheInitialized(itemBeforeCreate.ParentFolderId.Id);
}

function onCreateItemStart(
    itemBeforeCreate: CalendarEvent,
    allowLocalCreate: boolean,
    rangeToCreate: DateRange
) {
    if (allowLocalCreate) {
        const folderId = itemBeforeCreate.ParentFolderId.Id;

        if (itemBeforeCreate.Recurrence) {
            let expandedItems = expandRecurringItem(itemBeforeCreate, rangeToCreate);
            prepareSeriesItemsForInstantUpdate(expandedItems.concat(itemBeforeCreate));
            let itemsWithLies = expandedItems.map(occurrence => ({
                ...occurrence,
                CalendarItemType: 'Occurrence' as CalendarItemTypeTypeEx,
            }));
            addCalendarEventsWithinCurrentLockedDateRanges(folderId, itemsWithLies);
            return;
        } else {
            prepareItemForInstantUpdate(itemBeforeCreate);
            addCalendarEventsWithinCurrentLockedDateRanges(folderId, [itemBeforeCreate]);
        }

        if (isFeatureEnabled('cal-surface-notificationBar')) {
            lazyShowCalendarNotification.import().then(showCalendarNotification => {
                showCalendarNotification(
                    'instantCalendarEventCreate',
                    strings.calendarNotificationsBarCreateEvent,
                    { icon: ControlIcons.CheckMark }
                );
            });
        }
    }
}

function onCreateItemSucceed(
    itemBeforeCreate: CalendarEvent,
    itemAfterCreate: CalendarEvent,
    allowLocalCreate: boolean
) {
    // Ensure we have a parent folder id as the create operation may not return this
    if (!itemAfterCreate.ParentFolderId) {
        itemAfterCreate.ParentFolderId = { ...itemBeforeCreate.ParentFolderId };
    }

    if (allowLocalCreate) {
        if (itemAfterCreate.Recurrence) {
            // if item is recurring, do nothing- we don't get the expanded items back yet so need to wait for notification to allow the
            // occurrences to be editable
            return;
        }

        const folderId = itemAfterCreate.ParentFolderId.Id;
        removeCalendarEventsFromEventsCacheMatchingFilter(
            folderId,
            event => event.ItemId.Id === itemBeforeCreate.ItemId.Id
        );
        addOrModifyItemInCache(
            itemAfterCreate,
            true /* treat as single item */,
            false /* add to cache if not present */
        );
    }
}

function onCreateItemFailed(
    itemBeforeSave: CalendarEvent,
    errorMessage: string,
    allowLocalCreate: boolean,
    retryFunction: () => void
) {
    if (allowLocalCreate) {
        const folderId = itemBeforeSave.ParentFolderId.Id;
        if (itemBeforeSave.Recurrence) {
            // if item is recurring, remove the whole series
            removeCalendarEventsFromEventsCacheMatchingFilter(folderId, item => {
                return item.InstanceKey == itemBeforeSave.InstanceKey;
            });
        } else {
            removeCalendarEventsFromEventsCacheMatchingFilter(
                folderId,
                event => event.ItemId.Id === itemBeforeSave.ItemId.Id
            );
        }
    }

    lazyShowCalendarNotificationWithButtonCallback
        .import()
        .then(showNotificationWithButtonCallback => {
            showNotificationWithButtonCallback(
                'instantCalendarEventCreate',
                errorMessage,
                strings.tryAgainLabel,
                () => retryFunction(),
                ControlIcons.Error
            );
        });
}
